import { Type } from "@themes/pollinator/ui/Type";
import { Button } from "@themes/pollinator/ui/Button";
import type { Meeting } from "@core/features/meeting";
import type { TimezoneOption } from "@themes/pollinator/ui/TimezoneSelect";
import { ProgramCalendarEventList } from "@platform/features/programScheduling";
import { SubStepLayout } from "@platform/features/onboarding/components/SubStepLayout";

export type Props = {
  /** User's scheduled meetings */
  meetings: Meeting[];
  /** User's timezone option */
  userTimezone?: TimezoneOption;
  /** Handler when user adds a calendar event */
  onAddCalendarEvent?: (
    meetingId: number | string | "all",
    calendarType: "google" | "ical"
  ) => void;
  /** Handler for continue/next button */
  onContinue: () => void;
  /** Handler for back button (hidden if undefined) */
  onBack?: () => void;
  /** Handler for skip button (hidden if undefined) */
  onSkip?: () => void;
  /** Current step index (0-indexed) */
  stepIndex: number;
  /** Total steps */
  totalSteps: number;
};

/**
 * Presentational add to calendar sub-step for schedule onboarding.
 * Shows user's scheduled meetings and allows adding to calendar.
 */
export function AddToCalendarSubStep({
  meetings,
  userTimezone,
  onAddCalendarEvent,
  onContinue,
  onBack,
  onSkip,
  stepIndex,
  totalSteps,
}: Props) {
  const hasMeetings = meetings && meetings.length > 0;

  return (
    <SubStepLayout
      title="Add to Calendar"
      description="Add your scheduled meetings to your calendar so you don't miss them."
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      onPrimaryClick={onContinue}
      onBack={onBack}
      onSkip={onSkip}
      primaryButtonText="Continue"
    >
      {hasMeetings ? (
        <ProgramCalendarEventList
          meetings={meetings}
          userTimezone={userTimezone}
          onAddCalendarEvent={onAddCalendarEvent ?? (() => {})}
        />
      ) : (
        <div
          className="flex flex-col items-center justify-center py-8 gap-4"
          data-testid="no-meetings-message"
        >
          <Type variant="body1" className="text-brand-700 text-center">
            No meetings scheduled yet.
          </Type>
          <Type variant="body2" className="text-brand-600 text-center">
            Your meetings will appear here once they've been scheduled.
          </Type>
          <Button variant="ghost" onClick={onContinue}>
            Continue anyway
          </Button>
        </div>
      )}
    </SubStepLayout>
  );
}

export default AddToCalendarSubStep;
